// withAuth.tsx
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { showNotification } from "@/store/app/reducer";
import store from "@/store";
import {
  removeLocalStorageItems,
  getSubdomain,
  concatAndEncodeBase64,
} from "@/common/utils/utils";
import {
  XSM_APPLICATION,
  XSM_BASE_URL,
  XSM_DOMAIN,
  XSM_ORIGIN,
  CLIENT_SECRET,
  CLIENT_URL,
} from "@/common/constants/auth";
import {
  PROD_CHECK_INTERVAL,
  PROD_REFRESH_THRESHOLD,
} from "@/common/constants/constants";
import { useAuth } from "@/modules/auth/components/common/useAuth";

const withAuth = (WrappedComponent) => {
  return function WithAuth(props) {
    const navigate = useNavigate();
    const subdomain = getSubdomain();
    const { initialAuthorization } = useAuth();
    const [isActive, setIsActive] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const refreshTokenRef = useRef(localStorage.getItem("refresh_token") || "");
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const isAuthenticated = localStorage.getItem("access_token");
    // Calculate thresholds based on environment
    const refreshThreshold = PROD_REFRESH_THRESHOLD;
    const checkInterval = PROD_CHECK_INTERVAL;

    const logoutUser = useCallback(async () => {
      removeLocalStorageItems();
      await initialAuthorization();

      navigate("/login");
      store.dispatch(
        showNotification({
          showNotification: true,
          message: "Session Expired",
          type: "error",
        })
      );
    }, [navigate]);

    const refreshAccessToken = useCallback(async () => {
      if (isRefreshing) return;
      setIsRefreshing(true);

      //base64 authorization
      const basicAuthorozationSecret = concatAndEncodeBase64(
        CLIENT_URL,
        CLIENT_SECRET
      );

      try {
        const response = await fetch(
          `${XSM_BASE_URL}/xsm-api/v1/proxy/oauth2/token`,
          {
            method: "POST",
            headers: {
              Authorization: `Basic ${basicAuthorozationSecret}`,
              "Content-Type": "application/x-www-form-urlencoded",
              "xsm-application": XSM_APPLICATION,
              "Xsm-Domain": subdomain ?? XSM_DOMAIN,
              "Xsm-iscompressed": "true",
              "X-Original-Origin": XSM_ORIGIN,
            },
            body: new URLSearchParams({
              grant_type: "refresh_token",
              refresh_token: refreshTokenRef.current,
            }),
          }
        );

        if (!response.ok) throw new Error("Failed to refresh access token");

        const data = await response.json();

        // For local testing, override expires_in with our test value
        const expiresInSeconds = data.expires_in;

        const expiresInMs = expiresInSeconds * 1000;
        const expirationTime = Date.now() + expiresInMs;

        localStorage.setItem("access_token", data.access_token);
        localStorage.setItem("refresh_token", data.refresh_token);
        localStorage.setItem("expires_in", expirationTime.toString());

        refreshTokenRef.current = data.refresh_token;
      } catch (error) {
        logoutUser();
      } finally {
        setIsRefreshing(false);
      }
    }, [isRefreshing, subdomain, logoutUser]);

    const checkTokenStatus = useCallback(() => {
      const storedExpiry = Number(localStorage.getItem("expires_in"));
      if (!isAuthenticated || !storedExpiry) {
        logoutUser();
        return;
      }

      const currentTime = Date.now();
      const timeLeft = storedExpiry - currentTime;

      // Format time for better debugging
      const minutesLeft = Math.floor(timeLeft / 60000);
      const secondsLeft = Math.floor((timeLeft % 60000) / 1000);

      // console.log(`Token status - ${minutesLeft}m ${secondsLeft}s remaining`, {
      //   expiresAt: new Date(storedExpiry).toLocaleTimeString(),
      //   currentTime: new Date(currentTime).toLocaleTimeString(),
      // });

      if (timeLeft <= 0) {
        console.log("Token expired - logging out");
        logoutUser();
      } else if (timeLeft <= refreshThreshold && isActive) {
        console.log(
          `Token about to expire (${minutesLeft}m ${secondsLeft}s left) - refreshing...`
        );
        refreshAccessToken();
      }
    }, [
      isActive,
      isAuthenticated,
      refreshAccessToken,
      logoutUser,
      refreshThreshold,
    ]);

    useEffect(() => {
      if (isAuthenticated) {
        // Initial check
        checkTokenStatus();

        // Set up periodic checking
        intervalRef.current = setInterval(checkTokenStatus, checkInterval);
      }

      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
      };
    }, [isAuthenticated, checkTokenStatus, checkInterval]);

    useEffect(() => {
      const handleVisibilityChange = () => {
        const isNowActive = document.visibilityState === "visible";
        setIsActive(isNowActive);
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () => {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
      };
    }, []);

    if (!isAuthenticated) {
      return null; // or redirect to login
    }

    return <WrappedComponent {...props} />;
  };
};

export default withAuth;
